/**
 * Auto-generated service - DO NOT EDIT MANUALLY
 * Generated from: case_record.ts
 * Run: pnpm gen
 */

import { API_METHODS } from '../constants/api-methods';
import { getApiPath } from '../path';
import {
  requirePostForm,
  requireStream,
  type RequestOptions,
  type StreamOptions,
  type Transport,
} from '../transport';

import type {
  AdminSearchCaseRecordsRequest,
  AdminSearchCaseRecordsResponse,
  AdminUpdateCaseStateRequest,
  AdminUpdateCaseStateResponse,
  AdminUpdateSLARequest,
  AdminUpdateSLAResponse,
  AdminUpdateStatusFlagsRequest,
  AdminUpdateStatusFlagsResponse,
  BulkDeleteCaseRecordsRequest,
  BulkDeleteCaseRecordsResponse,
  CancelCaseRecordRequest,
  CancelCaseRecordResponse,
  CloneCaseRecordRequest,
  CloneCaseRecordResponse,
  CreateCaseRecordRequest,
  CreateCaseRecordResponse,
  CreateCaseRecordWithFaxRequest,
  CreateCaseRecordWithFaxResponse,
  DeleteCaseRecordRequest,
  DeleteCaseRecordResponse,
  ExecuteActionRequest,
  ExecuteActionResponse,
  ExportCaseRecordsRequest,
  ExportCaseRecordsResponse,
  FindCaseRecordsRequest,
  FindCaseRecordsResponse,
  FindOrganizationCaseRecordsRequest,
  FindOrganizationCaseRecordsResponse,
  GetCaseCountsRequest,
  GetCaseCountsResponse,
  GetCaseRecordHistoryRequest,
  GetCaseRecordHistoryResponse,
  GetCaseRecordRequest,
  GetCaseRecordResponse,
  GetCaseRecordTimelineRequest,
  GetCaseRecordTimelineResponse,
  GetCurrentUserAssignedCaseRecordsRequest,
  GetCurrentUserAssignedCaseRecordsResponse,
  GetOrganizationCaseCountsRequest,
  GetOrganizationCaseCountsResponse,
  LinkToCaseRequest,
  LinkToCaseResponse,
  ListMyOrganizationCasesRequest,
  ListMyOrganizationCasesResponse,
  MarkAsAdverseEventRequest,
  MarkAsAdverseEventResponse,
  MigrateCaseRecordToMedBVRequest,
  MigrateCaseRecordToMedBVResponse,
  SearchCaseRecordsRequest,
  SearchCaseRecordsResponse,
  StartCaseRecordRequest,
  StartCaseRecordResponse,
  TriggerProcessRecordingRequest,
  TriggerProcessRecordingResponse,
  UpdateAssignedRequest,
  UpdateAssignedResponse,
  UpdateCaseRecordRequest,
  UpdateCaseRecordResponse,
  UpdateDispositionCodeRequest,
  UpdateDispositionCodeResponse,
  UpdateInputDataRequest,
  UpdateInputDataResponse,
  UpdateOutputDataRequest,
  UpdateOutputDataResponse,
  WatchCaseRecordsRequest,
  WatchCaseRecordsResponse,
} from '../types/case_record';

export const createCaseRecordService = (transport: Transport) => ({
  async createCaseRecord(
    request: CreateCaseRecordRequest,
    options: RequestOptions = {},
  ): Promise<CreateCaseRecordResponse> {
    const { organization_id, program_id, ...body } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.CreateCaseRecord, {
      organization_id,
      program_id,
    });
    return transport.post<CreateCaseRecordResponse>(url, body, options);
  },

  async findCaseRecords(
    request: FindCaseRecordsRequest,
    options: RequestOptions = {},
  ): Promise<FindCaseRecordsResponse> {
    const { organization_id, program_id, ...params } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.FindCaseRecords, {
      organization_id,
      program_id,
    });
    return transport.get<FindCaseRecordsResponse>(url, { params, ...options });
  },

  async exportCaseRecords(
    request: ExportCaseRecordsRequest,
    options: RequestOptions = {},
  ): Promise<ExportCaseRecordsResponse> {
    const { organization_id, program_id, ...params } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.ExportCaseRecords, {
      organization_id,
      program_id,
    });
    return transport.get<ExportCaseRecordsResponse>(url, { params, ...options });
  },

  async findOrganizationCaseRecords(
    request: FindOrganizationCaseRecordsRequest,
    options: RequestOptions = {},
  ): Promise<FindOrganizationCaseRecordsResponse> {
    const { organization_id, ...params } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.FindOrganizationCaseRecords, {
      organization_id,
    });
    return transport.get<FindOrganizationCaseRecordsResponse>(url, { params, ...options });
  },

  async getOrganizationCaseCounts(
    request: GetOrganizationCaseCountsRequest,
    options: RequestOptions = {},
  ): Promise<GetOrganizationCaseCountsResponse> {
    const { organization_id, ...params } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.GetOrganizationCaseCounts, {
      organization_id,
    });
    return transport.get<GetOrganizationCaseCountsResponse>(url, { params, ...options });
  },

  async searchCaseRecords(
    request: SearchCaseRecordsRequest,
    options: RequestOptions = {},
  ): Promise<SearchCaseRecordsResponse> {
    const { organization_id, program_id, ...params } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.SearchCaseRecords, {
      organization_id,
      program_id,
    });
    return transport.get<SearchCaseRecordsResponse>(url, { params, ...options });
  },

  async listMyOrganizationCases(
    request: ListMyOrganizationCasesRequest,
    options: RequestOptions = {},
  ): Promise<ListMyOrganizationCasesResponse> {
    const { organization_id, ...params } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.ListMyOrganizationCases, {
      organization_id,
    });
    return transport.get<ListMyOrganizationCasesResponse>(url, { params, ...options });
  },

  async getCurrentUserAssignedCaseRecords(
    request: GetCurrentUserAssignedCaseRecordsRequest,
    options: RequestOptions = {},
  ): Promise<GetCurrentUserAssignedCaseRecordsResponse> {
    const { organization_id, program_id, ...params } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.GetCurrentUserAssignedCaseRecords, {
      organization_id,
      program_id,
    });
    return transport.get<GetCurrentUserAssignedCaseRecordsResponse>(url, { params, ...options });
  },

  async getCaseRecord(
    request: GetCaseRecordRequest,
    options: RequestOptions = {},
  ): Promise<GetCaseRecordResponse> {
    const { organization_id, program_id, case_record_id } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.GetCaseRecord, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.get<GetCaseRecordResponse>(url, options);
  },

  async getCaseRecordHistory(
    request: GetCaseRecordHistoryRequest,
    options: RequestOptions = {},
  ): Promise<GetCaseRecordHistoryResponse> {
    const { organization_id, program_id, case_record_id, ...params } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.GetCaseRecordHistory, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.get<GetCaseRecordHistoryResponse>(url, { params, ...options });
  },

  async updateCaseRecord(
    request: UpdateCaseRecordRequest,
    options: RequestOptions = {},
  ): Promise<UpdateCaseRecordResponse> {
    const { organization_id, program_id, case_record_id, ...body } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.UpdateCaseRecord, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.post<UpdateCaseRecordResponse>(url, body, options);
  },

  async cancelCaseRecord(
    request: CancelCaseRecordRequest,
    options: RequestOptions = {},
  ): Promise<CancelCaseRecordResponse> {
    const { organization_id, program_id, case_record_id } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.CancelCaseRecord, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.post<CancelCaseRecordResponse>(url, {}, options);
  },

  async updateAssigned(
    request: UpdateAssignedRequest,
    options: RequestOptions = {},
  ): Promise<UpdateAssignedResponse> {
    const { organization_id, program_id, case_record_id, ...body } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.UpdateAssigned, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.post<UpdateAssignedResponse>(url, body, options);
  },

  async updateOutputData(
    request: UpdateOutputDataRequest,
    options: RequestOptions = {},
  ): Promise<UpdateOutputDataResponse> {
    const { organization_id, program_id, case_record_id, ...body } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.UpdateOutputData, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.post<UpdateOutputDataResponse>(url, body, options);
  },

  async updateInputData(
    request: UpdateInputDataRequest,
    options: RequestOptions = {},
  ): Promise<UpdateInputDataResponse> {
    const { organization_id, program_id, case_record_id, ...body } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.UpdateInputData, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.post<UpdateInputDataResponse>(url, body, options);
  },

  async updateDispositionCode(
    request: UpdateDispositionCodeRequest,
    options: RequestOptions = {},
  ): Promise<UpdateDispositionCodeResponse> {
    const { organization_id, program_id, case_record_id, ...body } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.UpdateDispositionCode, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.post<UpdateDispositionCodeResponse>(url, body, options);
  },

  async executeAction(
    request: ExecuteActionRequest,
    options: RequestOptions = {},
  ): Promise<ExecuteActionResponse> {
    const { organization_id, program_id, case_record_id, ...body } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.ExecuteAction, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.post<ExecuteActionResponse>(url, body, options);
  },

  async getCaseCounts(
    request: GetCaseCountsRequest,
    options: RequestOptions = {},
  ): Promise<GetCaseCountsResponse> {
    const { organization_id, program_id, ...params } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.GetCaseCounts, {
      organization_id,
      program_id,
    });
    return transport.get<GetCaseCountsResponse>(url, { params, ...options });
  },

  async deleteCaseRecord(
    request: DeleteCaseRecordRequest,
    options: RequestOptions = {},
  ): Promise<DeleteCaseRecordResponse> {
    const { organization_id, program_id, case_record_id } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.DeleteCaseRecord, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.delete<DeleteCaseRecordResponse>(url, options);
  },

  async bulkDeleteCaseRecords(
    request: BulkDeleteCaseRecordsRequest,
    options: RequestOptions = {},
  ): Promise<BulkDeleteCaseRecordsResponse> {
    const { organization_id, program_id, ...body } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.BulkDeleteCaseRecords, {
      organization_id,
      program_id,
    });
    return transport.post<BulkDeleteCaseRecordsResponse>(url, body, options);
  },

  async createCaseRecordWithFax(
    request: CreateCaseRecordWithFaxRequest,
    options: RequestOptions = {},
  ): Promise<CreateCaseRecordWithFaxResponse> {
    const { organization_id, program_id, ...data } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.CreateCaseRecordWithFax, {
      organization_id,
      program_id,
    });
    const formData = new FormData();
    if (data.workflow_id !== undefined) {
      formData.append('workflow_id', String(data.workflow_id));
    }
    if (data.reference_id !== undefined) {
      formData.append('reference_id', String(data.reference_id));
    }
    if (data.fax_file) {
      // Handle File, Blob, or Uint8Array (from proto bytes)
      const fileValue =
        data.fax_file instanceof Blob ? data.fax_file : new Blob([data.fax_file] as BlobPart[]);
      formData.append('fax_file', fileValue);
    }
    if (data.fax_filename !== undefined) {
      formData.append('fax_filename', String(data.fax_filename));
    }
    if (data.fax_content_type !== undefined) {
      formData.append('fax_content_type', String(data.fax_content_type));
    }
    if (data.fax_uuid !== undefined) {
      formData.append('fax_uuid', String(data.fax_uuid));
    }
    if (data.fax_received_timestamp !== undefined) {
      formData.append('fax_received_timestamp', String(data.fax_received_timestamp));
    }
    const postForm = requirePostForm(transport, 'CaseRecordsService.CreateCaseRecordWithFax');
    return postForm<CreateCaseRecordWithFaxResponse>(url, formData, options);
  },

  async markAsAdverseEvent(
    request: MarkAsAdverseEventRequest,
    options: RequestOptions = {},
  ): Promise<MarkAsAdverseEventResponse> {
    const { organization_id, program_id, case_record_id, ...body } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.MarkAsAdverseEvent, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.post<MarkAsAdverseEventResponse>(url, body, options);
  },

  async linkToCase(
    request: LinkToCaseRequest,
    options: RequestOptions = {},
  ): Promise<LinkToCaseResponse> {
    const { organization_id, program_id, case_record_id, ...body } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.LinkToCase, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.post<LinkToCaseResponse>(url, body, options);
  },

  async triggerProcessRecording(
    request: TriggerProcessRecordingRequest,
    options: RequestOptions = {},
  ): Promise<TriggerProcessRecordingResponse> {
    const { organization_id, program_id, case_record_id, ...body } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.TriggerProcessRecording, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.post<TriggerProcessRecordingResponse>(url, body, options);
  },

  async cloneCaseRecord(
    request: CloneCaseRecordRequest,
    options: RequestOptions = {},
  ): Promise<CloneCaseRecordResponse> {
    const { organization_id, program_id, case_record_id, ...body } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.CloneCaseRecord, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.post<CloneCaseRecordResponse>(url, body, options);
  },

  async migrateCaseRecordToMedBv(
    request: MigrateCaseRecordToMedBVRequest,
    options: RequestOptions = {},
  ): Promise<MigrateCaseRecordToMedBVResponse> {
    const { organization_id, program_id, case_record_id, ...body } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.MigrateCaseRecordToMedBV, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.post<MigrateCaseRecordToMedBVResponse>(url, body, options);
  },

  async startCaseRecord(
    request: StartCaseRecordRequest,
    options: RequestOptions = {},
  ): Promise<StartCaseRecordResponse> {
    const { organization_id, program_id, case_record_id, ...body } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.StartCaseRecord, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.post<StartCaseRecordResponse>(url, body, options);
  },

  watchCaseRecords(
    request: WatchCaseRecordsRequest,
    options: StreamOptions = {},
  ): AsyncIterable<WatchCaseRecordsResponse> {
    const { organization_id, program_id, ...body } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.WatchCaseRecords, {
      organization_id,
      program_id,
    });
    const stream = requireStream(transport, 'CaseRecordsService.WatchCaseRecords');
    return stream<WatchCaseRecordsResponse>(url, body, options);
  },

  async getCaseRecordTimeline(
    request: GetCaseRecordTimelineRequest,
    options: RequestOptions = {},
  ): Promise<GetCaseRecordTimelineResponse> {
    const { organization_id, program_id, case_record_id, ...params } = request;
    const url = getApiPath(API_METHODS.CaseRecordsService.GetCaseRecordTimeline, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.get<GetCaseRecordTimelineResponse>(url, { params, ...options });
  },

  async adminUpdateSla(
    request: AdminUpdateSLARequest,
    options: RequestOptions = {},
  ): Promise<AdminUpdateSLAResponse> {
    const { case_record_id, ...body } = request;
    const url = getApiPath(API_METHODS.AdminCaseRecordService.AdminUpdateSLA, { case_record_id });
    return transport.post<AdminUpdateSLAResponse>(url, body, options);
  },

  async adminUpdateCaseState(
    request: AdminUpdateCaseStateRequest,
    options: RequestOptions = {},
  ): Promise<AdminUpdateCaseStateResponse> {
    const { case_record_id, ...body } = request;
    const url = getApiPath(API_METHODS.AdminCaseRecordService.AdminUpdateCaseState, {
      case_record_id,
    });
    return transport.post<AdminUpdateCaseStateResponse>(url, body, options);
  },

  async adminSearchCaseRecords(
    request: AdminSearchCaseRecordsRequest,
    options: RequestOptions = {},
  ): Promise<AdminSearchCaseRecordsResponse> {
    const url = getApiPath(API_METHODS.AdminCaseRecordService.AdminSearchCaseRecords);
    return transport.get<AdminSearchCaseRecordsResponse>(url, { params: request, ...options });
  },

  async adminUpdateStatusFlags(
    request: AdminUpdateStatusFlagsRequest,
    options: RequestOptions = {},
  ): Promise<AdminUpdateStatusFlagsResponse> {
    const { case_record_id, ...body } = request;
    const url = getApiPath(API_METHODS.AdminCaseRecordService.AdminUpdateStatusFlags, {
      case_record_id,
    });
    return transport.post<AdminUpdateStatusFlagsResponse>(url, body, options);
  },
});

export type CaseRecordService = ReturnType<typeof createCaseRecordService>;
